import { useCallback, useEffect, useState } from 'react';

import { LIMITS, STORAGE_KEYS } from '@/config';
import type { DictKey } from '@/i18n/en';
import { useT } from '@/i18n/LocaleProvider';

import type { ProposalResponse } from './api';

type TFn = (key: DictKey) => string;

export type PendingProposal = {
  id: string;
  kind: ProposalResponse['kind'];
  status: ProposalResponse['status'];
  label: string;
  submittedAt: string;
};

type PendingProposalMap = Record<string, PendingProposal[]>;

export function usePendingProposals(accountID?: string) {
  const t = useT();
  const [proposals, setProposals] = useState<PendingProposal[]>(() =>
    accountID ? readPendingProposals(accountID) : [],
  );

  useEffect(() => {
    setProposals(accountID ? readPendingProposals(accountID) : []);
  }, [accountID]);

  const rememberProposal = useCallback(
    (proposal: ProposalResponse) => {
      if (!accountID) {
        return;
      }

      const next = rememberPendingProposal(accountID, proposal, t);
      setProposals(next);
    },
    [accountID, t],
  );

  return { proposals, rememberProposal };
}

function readPendingProposals(accountID: string): PendingProposal[] {
  return readPendingProposalMap()[accountID] ?? [];
}

function rememberPendingProposal(
  accountID: string,
  proposal: ProposalResponse,
  t: TFn,
): PendingProposal[] {
  const stored = readPendingProposalMap();
  const nextProposal: PendingProposal = {
    id: proposal.id,
    kind: proposal.kind,
    status: proposal.status,
    label: proposalLabel(proposal, t),
    submittedAt: proposal.created_at,
  };
  const withoutDuplicate = (stored[accountID] ?? []).filter((item) => item.id !== proposal.id);
  const next = [nextProposal, ...withoutDuplicate]
    .filter((item) => item.status === 'pending')
    .slice(0, LIMITS.maxStoredProposals);

  stored[accountID] = next;
  writePendingProposalMap(stored);

  return next;
}

function readPendingProposalMap(): PendingProposalMap {
  if (typeof localStorage === 'undefined') {
    return {};
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEYS.pendingProposals);

    if (!raw) {
      return {};
    }

    return JSON.parse(raw) as PendingProposalMap;
  } catch {
    return {};
  }
}

function writePendingProposalMap(value: PendingProposalMap) {
  if (typeof localStorage === 'undefined') {
    return;
  }

  localStorage.setItem(STORAGE_KEYS.pendingProposals, JSON.stringify(value));
}

function proposalLabel(proposal: ProposalResponse, t: TFn): string {
  if (proposal.kind === 'new') {
    return proposal.title || proposal.slug || t('moderation_kind_new');
  }

  return proposal.summary || proposal.page_id || t('moderation_kind_edit');
}
